import React, { useMemo } from "react";
import { useParams, useRouteMatch } from "react-router";
import { NavLink } from "react-router-dom";

import { faHubspot } from "@awesome.me/kit-4c83d50d0d/icons/classic/brands";
import {
	faBookmark,
	faCheckSquare,
	faCommentAltLines,
	faFileLines,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faPrivate, faSalesforceOutline } from "@awesome.me/kit-4c83d50d0d/icons/kit/custom";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { type TabKey, useAllowedSeriesSidePanelTabs } from "~/meetings/SeriesSidePanel/SeriesSidePanel";
import { useSingleton } from "~/ui/Tooltip";
import Tooltip from "~/ui/Tooltip";

import type { SeriesSidePanel_allowedTabs_stream$key } from "~/__generated__/SeriesSidePanel_allowedTabs_stream.graphql";
import type { SeriesSidePanel_allowedTabs_viewer$key } from "~/__generated__/SeriesSidePanel_allowedTabs_viewer.graphql";

import styles from "./Tabs.module.css";

const TABS = {
	notes: "Notes",
	"private-notes": "Private Notes",
	"action-items": "Action Items",
	automations: "Automations",
	bookmarks: "Bookmarks",
	hubspot: "Hubspot",
	salesforce: "Salesforce",
	comments: "Comments",
} as const satisfies Record<"notes" | TabKey, string>;
type CompanionModeTabKey = keyof typeof TABS;

interface TabProps {
	tab: CompanionModeTabKey;
	label: string;
	icon: IconDefinition;
	tooltipSingleton: ReturnType<typeof useSingleton>[1];
}

function Tab({ tab, label, icon, tooltipSingleton }: TabProps) {
	const { noteId } = useParams<{ noteId: string; tab: CompanionModeTabKey }>();
	const url = useMemo(() => `/companion-mode/${noteId}/${tab}/`, [noteId, tab]);
	const isSelected = useRouteMatch({ path: url }) !== null;

	// TODO: Reuse our buttons? Make a new NavLink button?
	return (
		// TODO: For some reason this "disabled" doesn't work 🤔
		<Tooltip singleton={tooltipSingleton} content={label} disabled={isSelected}>
			<NavLink to={url} activeClassName={styles.selected} className={styles.tab}>
				<span>
					<FontAwesomeIcon icon={icon} />
				</span>
				{isSelected && <span>{label}</span>}
			</NavLink>
		</Tooltip>
	);
}

export default function Tabs({
	viewer,
	stream,
}: {
	viewer: SeriesSidePanel_allowedTabs_viewer$key;
	stream: SeriesSidePanel_allowedTabs_stream$key;
}) {
	const allowedTabs = useAllowedSeriesSidePanelTabs(viewer, stream || null);
	const [source, target] = useSingleton();
	const showCommentsTab = useRouteMatch({ path: "/companion-mode/:noteId/comments/:threadKey" }) !== null;

	return (
		<div className={styles.tabs}>
			<Tooltip singleton={source} placement="bottom" />
			<Tab tab="notes" label="Notes" icon={faFileLines} tooltipSingleton={target} />
			<Tab tab="private-notes" label="Private notes" icon={faPrivate} tooltipSingleton={target} />
			<Tab tab="action-items" label="Action items" icon={faCheckSquare} tooltipSingleton={target} />
			<Tab tab="bookmarks" label="Bookmarks" icon={faBookmark} tooltipSingleton={target} />
			{allowedTabs.hubspot && <Tab tab="hubspot" label="HubSpot" icon={faHubspot} tooltipSingleton={target} />}
			{allowedTabs.salesforce && (
				<Tab tab="salesforce" label="Salesforce" icon={faSalesforceOutline} tooltipSingleton={target} />
			)}
			{showCommentsTab && <Tab tab="comments" label="Comments" icon={faCommentAltLines} tooltipSingleton={target} />}
		</div>
	);
}
